import { Z } from "@repo/core/utils/zod.utils";
import { RefinementCtx } from "zod";
import { isAfter, isValid, parse, startOfDay } from "date-fns";
import {
  PARENT_NAME_MAX_LENGTH,
  PARENT_NAME_MESSAGES,
  hasMinimumParentNameWords
} from "@repo/core/utils/parentName.utils";
import {
  ADDITIONAL_PHONE_EQUALS_PRIMARY_MESSAGE,
  areNormalizedPhonesEqual
} from "@repo/core/utils/phone.utils";

/**
 * Validates that a Brazilian cell phone number starts with 8 or 9 after the area code.
 * Expected format: (XX) 9XXXX-XXXX or (XX) 8XXXX-XXXX
 */
const validateCellPhone = (cellPhone: string, ctx: RefinementCtx) => {
  const digits = cellPhone.replace(/\D/g, "");
  if (digits.length < 3) return;

  const firstDigitAfterDDD = digits[2];
  if (firstDigitAfterDDD !== "8" && firstDigitAfterDDD !== "9") {
    ctx.addIssue({
      code: "custom",
      message: "O número do celular deve iniciar com 8 ou 9."
    });
  }
};

/**
 * Validates that the phone contains only valid digits (10-11 digits for Brazilian format).
 * Does NOT validate if the number starts with 8 or 9.
 */
const validatePhoneDigitsOnly = (cellPhone: string, ctx: RefinementCtx) => {
  const digits = cellPhone.replace(/\D/g, "");
  if (digits.length < 3) return;

  if (digits.length < 10 || digits.length > 11) {
    ctx.addIssue({
      code: "custom",
      message: "Informe um número de telefone válido (10 ou 11 dígitos)."
    });
  }
};

/**
 * Validates Brazilian date format (DD/MM/YYYY or DD/MM/YY)
 * @param birthday - Date string in Brazilian format
 * @param ctx - Zod refinement context
 * @param options - Validation options
 */
const validateBrazilianDate = (
  birthday: string,
  ctx: RefinementCtx,
  options?: { minAge?: number; allowFuture?: boolean }
) => {
  if (!birthday || birthday.trim() === "") {
    return ctx.addIssue({
      code: "custom",
      message: "Data de nascimento inválida. Use o formato DD/MM/AAAA."
    });
  }

  // Parse Brazilian date format DD/MM/YYYY or DD/MM/YY
  const dateParts = birthday.split("/");
  if (dateParts.length !== 3) {
    ctx.addIssue({
      code: "custom",
      message: "Data de nascimento inválida. Use o formato DD/MM/AAAA."
    });
    return;
  }

  const day = parseInt(dateParts[0], 10);
  const month = parseInt(dateParts[1], 10);
  let year = parseInt(dateParts[2], 10);

  // Handle 2-digit years (assume 1900-1999 for years < 100)
  if (year < 100) {
    year = year < 50 ? 2000 + year : 1900 + year;
  }

  // Validate date components
  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    ctx.addIssue({
      code: "custom",
      message: "Data de nascimento inválida."
    });
    return;
  }

  // Create date object (month is 0-indexed in JavaScript Date)
  const date = new Date(year, month - 1, day);

  // Validate if date is valid
  if (
    isNaN(date.getTime()) ||
    date.getDate() !== day ||
    date.getMonth() !== month - 1 ||
    date.getFullYear() !== year
  ) {
    ctx.addIssue({
      code: "custom",
      message: "Data de nascimento inválida."
    });
    return;
  }

  // Validate if date is not in the future (unless explicitly allowed)
  if (!options?.allowFuture) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date > today) {
      ctx.addIssue({
        code: "custom",
        message: "A data de nascimento não pode ser futura."
      });
      return;
    }
  }

  // Validate minimum age (if specified)
  if (options?.minAge !== undefined) {
    const today = new Date();
    const age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    const dayDiff = today.getDate() - date.getDate();
    const actualAge =
      monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

    if (actualAge < options.minAge) {
      ctx.addIssue({
        code: "custom",
        message: `É necessário ter pelo menos ${options.minAge} anos.`
      });
      return;
    }
  }
};

/**
 * Validates Brazilian admission date format (DD/MM/YYYY).
 * Admission date cannot be after the current date; today is allowed.
 */
const validateAdmissionDate = (admissionDate: string, ctx: RefinementCtx) => {
  if (!admissionDate || admissionDate.trim() === "") {
    return ctx.addIssue({
      code: "custom",
      message: "Data de admissão inválida. Use o formato DD/MM/AAAA."
    });
  }

  const dateParts = admissionDate.split("/");
  if (dateParts.length !== 3) {
    ctx.addIssue({
      code: "custom",
      message: "Data de admissão inválida. Use o formato DD/MM/AAAA."
    });
    return;
  }

  const yearPart = dateParts[2];
  const format = yearPart.length <= 2 ? "dd/MM/yy" : "dd/MM/yyyy";
  const date = parse(admissionDate, format, new Date());

  if (!isValid(date)) {
    ctx.addIssue({
      code: "custom",
      message: "Data de admissão inválida."
    });
    return;
  }

  const today = startOfDay(new Date());
  const admissionDateNormalized = startOfDay(date);

  if (isAfter(admissionDateNormalized, today)) {
    ctx.addIssue({
      code: "custom",
      message: "A data de admissão não pode ser posterior à data atual."
    });
  }
};

export const cpfFormSchema = Z.object({
  cpf: Z.string({ required_error: "É necessário informar o CPF." }).cpf(
    "É necessário informar um CPF válido."
  )
});

export const cardFormSchema = Z.object({
  card: Z.string({
    required_error: "É necessário informar o número do cartão."
  }).min(16, { message: "É necessário informar o número do cartão." })
});

export const creditProposalPersonalDataSchema = Z.object({
  birthday: Z.string({
    required_error: "É necessário informar a data de nascimento."
  })
    .min(1, { message: "É necessário informar a data de nascimento." })
    .superRefine((birthday, ctx) => {
      validateBrazilianDate(birthday, ctx, { minAge: 18 });
    }),
  gender: Z.string()
    .trim()
    .min(1, { message: "É necessário informar o gênero." }),
  martialStatus: Z.string()
    .trim()
    .min(1, { message: "É necessário informar o estado civil." }),
  mothersName: Z.string()
    .min(1, { message: PARENT_NAME_MESSAGES.motherRequired })
    .max(PARENT_NAME_MAX_LENGTH, {
      message: PARENT_NAME_MESSAGES.motherMaxLength
    })
    .refine((value) => hasMinimumParentNameWords(value), {
      message: PARENT_NAME_MESSAGES.motherTwoWords
    }),
  fathersName: Z.string().max(PARENT_NAME_MAX_LENGTH, {
    message: PARENT_NAME_MESSAGES.fatherMaxLength
  }),
  documentNumber: Z.string()
    .trim()
    .min(1, {
      message: "Informe o número do documento (RG, CNH, etc.)."
    })
    .max(15, {
      message: "O número do documento deve ter no máximo 15 caracteres."
    })
});

/** Validates an optional residential phone (DDD + number digits). */
const optionalResidentialPhoneSchema = Z.string()
  .optional()
  .superRefine((val, ctx) => {
    if (!val || val.trim() === "") return;
    const digits = val.replace(/\D/g, "");
    if (digits.length < 10) {
      ctx.addIssue({
        code: "custom",
        message: "Informe DDD e número (mínimo 10 dígitos)."
      });
    }
  });

export const creditProposalAddressSchema = Z.object({
  residenceType: Z.string()
    .trim()
    .min(1, { message: "É necessário informar o tipo de residência." }),
  addressLine1: Z.string({
    required_error: "É necessário informar o endereço."
  }),
  addressLine2: Z.string().optional(),
  number: Z.string()
    .trim()
    .min(1, { message: "É necessário informar o número." })
    .max(7, {
      message: "O número da residência deve ter no máximo 7 dígitos."
    }),
  neighborhood: Z.string()
    .trim()
    .min(1, { message: "É necessário informar o bairro." }),
  city: Z.string()
    .trim()
    .min(1, { message: "É necessário informar a cidade." }),
  state: Z.string()
    .trim()
    .min(1, { message: "É necessário informar o estado." }),
  zipCode: Z.string()
    .trim()
    .min(1, { message: "É necessário informar o CEP." })
    .refine((value) => value.replace(/\D/g, "").length === 8, {
      message: "Informe um CEP válido com 8 dígitos."
    }),
  // Optional additional residential phone only (CRTPROPOSTAS.RESDDD2/RESFONE2/RESTIPOFONE2)
  residentialPhone2: optionalResidentialPhoneSchema,
  residentialPhone2Type: Z.string().optional(),
  primaryPhone: Z.string().optional()
}).superRefine(
  ({ residentialPhone2, residentialPhone2Type, primaryPhone }, ctx) => {
    const residentialPhone2Digits = residentialPhone2?.replace(/\D/g, "") ?? "";

    if (residentialPhone2Digits && !residentialPhone2Type?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "É necessário informar o tipo do telefone adicional.",
        path: ["residentialPhone2Type"]
      });
    }

    if (areNormalizedPhonesEqual(residentialPhone2, primaryPhone)) {
      ctx.addIssue({
        code: "custom",
        message: ADDITIONAL_PHONE_EQUALS_PRIMARY_MESSAGE,
        path: ["residentialPhone2"]
      });
    }
  }
);

export const creditProposalProfessionDataSchema = Z.object({
  professionalCategory: Z.string()
    .trim()
    .min(1, { message: "É necessário informar a categoria profissional." }),
  profession: Z.string()
    .trim()
    .min(1, { message: "É necessário informar a profissão." }),
  admissionDate: Z.string()
    .trim()
    .min(1, { message: "É necessário informar a data de admissão." })
    .superRefine(validateAdmissionDate),
  income: Z.string()
    .trim()
    .superRefine((income, ctx) => {
      const numericValue = Number(income);
      if (isNaN(numericValue) || numericValue <= 0) {
        ctx.addIssue({
          code: "custom",
          message: "O valor da renda deve ser maior que zero."
        });
      }
    })
});

export const transactionValueSchema = Z.object({
  purchaseValue: Z.string({
    required_error: "É necessário informar o valor da compra."
  }).superRefine((purchaseValue, ctx) => {
    const numericValue = Number(purchaseValue);
    if (isNaN(numericValue) || numericValue <= 0) {
      ctx.addIssue({
        code: "custom",
        message: "O valor da compra deve ser maior que zero."
      });
    }
  })
});

export const fidelityFormSchema = Z.object({
  bonusValue: Z.string()
    .optional()
    .refine(
      (val) => {
        if (!val) return true;
        const numericValue = Number(val);
        return !isNaN(numericValue) && numericValue >= 0;
      },
      { message: "Valor inválido" }
    )
});

export const installmentsFormSchema = Z.object({
  purchaseValue: Z.string({
    required_error: "É necessário informar o valor da compra."
  })
});

export const fidelityProposalFormDataSchema = Z.object({
  birthday: Z.string({
    required_error: "É necessário informar a data de nascimento."
  })
    .min(1, { message: "É necessário informar a data de nascimento." })
    .superRefine((birthday, ctx) => {
      validateBrazilianDate(birthday, ctx, { minAge: 18 });
    }),
  gender: Z.string({
    required_error: "É necessário informar o gênero."
  }).min(1, { message: "É necessário informar o gênero." }),
  martialStatus: Z.string().optional(),
  profession: Z.string().optional()
});

export const proposalSecurityFormDataSchema = Z.object({
  cpf: Z.string({
    required_error: "É necessário informar o CPF."
  }).cpf("É necessário informar um CPF válido."),
  fullName: Z.string({
    required_error: "É necessário informar o nome completo."
  }),
  cellPhone: Z.string({
    required_error: "É necesario informar o telefone."
  }).superRefine(validateCellPhone)
});

const NAME_ONLY_LETTERS_REGEX = /^[\p{L}\s]+$/u;

export const proposalInfoSecurityFormDataSchema = Z.object({
  cpf: Z.string({
    required_error: "É necessário informar o CPF."
  }).cpf("É necessário informar um CPF válido."),
  fullName: Z.string({
    required_error: "É necessário informar o nome completo."
  })
    .min(1, "É necessário informar o nome completo.")
    .refine(
      (val) => NAME_ONLY_LETTERS_REGEX.test(val.trim()),
      "O nome deve conter apenas letras e espaços."
    )
});

export const proposalPhoneFormDataSchema = Z.object({
  cellPhone: Z.string({
    required_error: "É necessário informar o telefone."
  }).superRefine((val, ctx) => {
    if (!val || val.replace(/\D/g, "").length < 11) {
      ctx.addIssue({
        code: "custom",
        message: "É necessário informar o DDD e o número do celular."
      });
      return;
    }
    validateCellPhone(val, ctx);
  })
});

export const proposalImplementationInvoiceDataFormSchema = Z.object({
  invoiceDate: Z.string({
    required_error: "É necessário selecionar o dia de vencimento."
  }).min(1, "É necessário selecionar o dia de vencimento."),
  invoiceDelivery: Z.enum(["E", "W", "T", "C"], {
    required_error: "É necessário informar o canal de envio da fatura."
  }),
  /**
   * Plain Z.string().email().optional() still validates "" as a string — invalid —
   * so submit never ran and Continuar appeared to do nothing. Required checks
   * are enforced via superRefine (validateEmails).
   */
  email: Z.preprocess(
    (val) =>
      typeof val !== "string" || val.trim() === "" ? undefined : val.trim(),
    Z.string().email("É necessário informar um e-mail válido.").optional()
  ),
  confirmEmail: Z.preprocess(
    (val) =>
      typeof val !== "string" || val.trim() === "" ? undefined : val.trim(),
    Z.string().optional()
  )
});

export const proposalImplementationPersonalReferenceFormDataSchema = Z.object({
  name: Z.string({
    required_error: "É necesario informar o nome."
  }),
  relationship: Z.string({
    required_error: "É necesario informar o relacionamento."
  }),
  cellPhone: Z.string({
    required_error: "É necesario informar o telefone."
  }).superRefine(validatePhoneDigitsOnly)
});
